import { useState, useEffect } from 'react';
import { useHeaderContent } from '../../../context/ContentContext';
import './Header.css';
import logo_440 from '../../../assets/images/logo_440.svg';
import icn_menu from '../../../assets/images/icn_menu.svg';
import hamburgerIcon from '../../../assets/images/hamburger.svg';
import userIcon from '../../../assets/images/auth-inactive.svg';
import Button from '../../common/Button/Button';

const SITE_HOME_URL = 'https://www.440artists.com/';

export default function Header({ variant, overlay = false }) {
    const { logo, navLinks, cta, cta1 } = useHeaderContent();
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close menu when clicking a nav link
    const handleNavClick = () => {
        setMenuOpen(false);
    };

    // Prevent background scrolling when menu is open
    useEffect(() => {
        if (menuOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none'; // Further prevent mobile touch scrolling
        } else {
            document.body.style.overflow = '';
            document.body.style.touchAction = '';
        }
        return () => {
            document.body.style.overflow = '';
            document.body.style.touchAction = '';
        };
    }, [menuOpen]);

    if (variant) {
        const logoImage = <img src={logo_440} alt="Site Logo" className="header__logo-image" />;

        return (
            <header className={`header header--${variant}${overlay ? ' header--overlay' : ''}`}>
                <div className="header__inner max-width">
                    {variant === 'minimal' ? logoImage : (
                        <>
                            <a href={SITE_HOME_URL} className="header__logo" rel="home" aria-label={logo.ariaLabel}>
                                {logoImage}
                            </a>
                            <Button href={cta.href} target="_blank" rel="noopener noreferrer" className="header__cta" icon={userIcon}>
                                {cta.label}
                            </Button>
                        </>
                    )}
                </div>
            </header>
        );
    }

    return (
        <>
            <header className={`header${scrolled ? ' header--scrolled' : ''}`}>
                <div className="header__inner max-width">
                    {/* Logo */}
                    <a href="/" className="header__logo" aria-label={logo.ariaLabel}>
                        <div className="header__logo-box">
                            <img
                                src={logo_440}
                                alt="Site Logo"
                                className="header__logo-image"
                            />
                        </div>
                    </a>

                    {/* Desktop Nav */}
                    <nav className="header__nav" aria-label="Main navigation">
                        {navLinks.map((link) => (
                            <a key={link.href} href={link.href} target={link.target} className="header__nav-link">
                                {link.label}
                            </a>
                        ))}
                    </nav>
                    <Button href={cta1.href} target="_top" className="header__cta">
                        {cta1.label}
                    </Button>

                    {/* CTA */}
                    <Button href={cta.href} target="_top" className="header__cta header__cta--secondary">
                        {cta.label}
                    </Button>

                    {/* Hamburger */}
                    <button
                        className={`header__hamburger${menuOpen ? ' header__hamburger--open' : ''}`}
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label="Toggle menu"
                        aria-expanded={menuOpen}
                    >
                        <img
                            src={hamburgerIcon}
                            alt="Menu"
                            className={`header__hamburger-icon ${menuOpen ? 'header__hamburger-icon--hidden' : ''}`}
                        />
                    </button>
                </div>
            </header>

            {/* Mobile Overlay/Modal Container */}
            <div className={`mobile-nav-modal${menuOpen ? ' mobile-nav-modal--open' : ''}`}>
                <div className="mobile-nav-overlay" onClick={() => setMenuOpen(false)} />

                <div className="mobile-nav-box">
                    <button
                        className="mobile-nav__close"
                        onClick={() => setMenuOpen(false)}
                        aria-label="Close menu"
                    >
                        <img src={icn_menu} alt="Close Menu" className="mobile-nav__close-icon" />
                    </button>

                    <nav className="mobile-nav__links" aria-label="Mobile navigation">
                        {navLinks.map((link) => (
                            <a key={link.href} href={link.href} className="mobile-nav__link" onClick={handleNavClick}>
                                {link.label}
                            </a>
                        ))}
                    </nav>

                    <div className="mobile-nav__ctas">
                        <Button href={cta1.href} target="_top" className="mobile-nav__cta">
                            {cta1.label}
                        </Button>
                        <Button href={cta.href} target="_top" className="mobile-nav__cta mobile-nav__cta--secondary">
                            {cta.label}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
}
